
const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_YMD_PATTERN = /^(\d{4}-\d{2}-\d{2})T/;

function isValidYmd(ymd: string): boolean {
  const match = YMD_PATTERN.exec(ymd);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function formatLocalYmd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function localTodayYmd(now = new Date()): string {
  return formatLocalYmd(now);
}

export function utcTodayYmd(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function nextYmd(ymd: string): string {
  const [year, month, day] = ymd.split("-").map(Number);
  return formatLocalYmd(new Date(year, month - 1, day + 1));
}

const shortDateFormatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

export function formatGameDateLabel(
  gameDate: string | null | undefined,
  todayYmd: string,
): string | undefined {
  if (!gameDate) return undefined;
  if (gameDate === "today") return "Today";
  if (gameDate === "tomorrow") return "Tomorrow";
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(gameDate);
  if (!isoMatch) return undefined;
  const ymd = `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  if (ymd === todayYmd) return "Today";
  if (ymd === nextYmd(todayYmd)) return "Tomorrow";
  return shortDateFormatter.format(new Date(Date.UTC(
    Number(isoMatch[1]),
    Number(isoMatch[2]) - 1,
    Number(isoMatch[3]),
  )));
}

export function buildLocalMidnightIso(ymd: string): string {
  const [year, month, day] = ymd.split("-").map(Number);
  const localMidnight = new Date(year, month - 1, day, 0, 0, 0, 0);
  const tzOffset = -localMidnight.getTimezoneOffset();
  const tzSign = tzOffset >= 0 ? "+" : "-";
  const tzHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, "0");
  const tzMinutes = String(Math.abs(tzOffset) % 60).padStart(2, "0");
  return `${ymd}T00:00:00${tzSign}${tzHours}:${tzMinutes}`;
}

export function extractYmd(dateStr: string): string | null {
  if (!dateStr) return null;

  if (isValidYmd(dateStr)) {
    return dateStr;
  }

  const isoMatch = ISO_YMD_PATTERN.exec(dateStr);
  return isoMatch && isValidYmd(isoMatch[1]) ? isoMatch[1] : null;
}

export function coerceGameDate(value: unknown): string {
  if (typeof value !== "string") return "";

  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "today" || trimmed === "tomorrow") {
    return trimmed;
  }

  const ymd = extractYmd(trimmed);
  if (!ymd) return "";
  if (trimmed === ymd) return trimmed;

  return Number.isFinite(Date.parse(trimmed)) ? trimmed : "";
}

export function resolveGameDateYmd(gameDate: string, todayYmd: string): string | null {
  const normalized = coerceGameDate(gameDate);
  if (!normalized) return null;
  if (normalized === "today") return todayYmd;
  if (normalized === "tomorrow") return nextYmd(todayYmd);
  return extractYmd(normalized);
}

export function eventMatchesGameDate(event: Date, gameDate: string, now = new Date()): boolean {
  if (!gameDate) return true;

  const selected = resolveGameDateYmd(gameDate, formatLocalYmd(now));
  if (!selected || Number.isNaN(event.getTime())) return false;
  return formatLocalYmd(event) === selected;
}

export const SOON_HOURS = [1, 3, 6] as const;

export type GameTime = { readonly days: readonly string[]; readonly within: number; readonly live: boolean };

export const ANY_GAME_TIME: GameTime = { days: [], within: 0, live: false };

export const LIVE_GAME_TIME: GameTime = { days: [], within: 0, live: true };

export function coerceGameDays(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  const days = new Set<string>();
  for (const item of values) {
    if (typeof item !== "string") continue;
    const trimmed = item.trim();
    const day = trimmed === "today" || trimmed === "tomorrow" ? trimmed : extractYmd(trimmed);
    if (day) days.add(day);
  }
  return [...days];
}

export function coerceStartsWithin(value: unknown): number {
  const hours = typeof value === "string" ? Number(value) : value;
  return SOON_HOURS.find((option) => option === hours) ?? 0;
}

export function activeGameDays(days: readonly string[], todayYmd: string): string[] {
  const active = new Set<string>();
  for (const day of days) {
    const ymd = resolveGameDateYmd(day, todayYmd);
    if (ymd !== null && ymd >= todayYmd) active.add(ymd);
  }
  return [...active].sort();
}

export function chosenGameDays(time: GameTime, todayYmd: string): string[] {
  return time.live || time.within > 0 ? [] : activeGameDays(time.days, todayYmd);
}

export function encodeGameDays(ymds: Iterable<string>, todayYmd: string): string[] {
  const tomorrowYmd = nextYmd(todayYmd);
  return [...new Set(ymds)]
    .filter((ymd) => ymd >= todayYmd)
    .sort()
    .map((ymd) => (ymd === todayYmd ? "today" : ymd === tomorrowYmd ? "tomorrow" : ymd));
}

export function gameTimeMatcher(time: GameTime, now = new Date()): ((startMs: number) => boolean) | null {
  if (time.live) return null;
  const days = new Set(chosenGameDays(time, formatLocalYmd(now)));
  const nowMs = now.getTime();
  const until = time.within > 0 ? nowMs + time.within * 3_600_000 : null;
  if (days.size === 0 && until === null) return null;
  return (startMs) => {
    if (!Number.isFinite(startMs)) return false;
    if (until !== null && (startMs < nowMs || startMs > until)) return false;
    return days.size === 0 || days.has(formatLocalYmd(new Date(startMs)));
  };
}

const dayFormatter = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

export function dayLabel(ymd: string, todayYmd: string): string {
  if (ymd === todayYmd) return "Today";
  if (ymd === nextYmd(todayYmd)) return "Tomorrow";
  const [year, month, day] = ymd.split("-").map(Number);
  return dayFormatter.format(new Date(Date.UTC(year, month - 1, day)));
}

export function gameTimeValue(time: GameTime): string {
  if (time.live) return "live";
  return time.within > 0 ? `next_${time.within}h` : time.days.join(",") || "all";
}

export function soonLabel(hours: number): string {
  return hours === 1 ? "Next hour" : `Next ${hours} hours`;
}

export function gameTimeLabel(time: GameTime, todayYmd: string): string {
  if (time.live) return "Live";
  if (time.within > 0) return soonLabel(time.within);
  const days = chosenGameDays(time, todayYmd);
  if (days.length === 0) return "Any time";
  const first = dayLabel(days[0], todayYmd);
  return days.length === 1 ? first : `${first} +${days.length - 1}`;
}

export type DayChoice = { readonly value: string; readonly label: string; readonly count: number | null };
export type SoonChoice = { readonly hours: number; readonly label: string; readonly count: number | null };
export type TimeChoices = { readonly days: readonly DayChoice[]; readonly soon: readonly SoonChoice[] };

export function startTimeChoices(starts: Iterable<readonly [startMs: number, rows: number]>, todayYmd: string, nowMs: number, live = false): TimeChoices {
  const days = new Map<string, number>([[todayYmd, 0], [nextYmd(todayYmd), 0]]);
  const soon = SOON_HOURS.map(() => 0);
  for (const [startMs, rows] of starts) {
    if (!Number.isFinite(startMs)) continue;
    const ymd = formatLocalYmd(new Date(startMs));
    if (ymd >= todayYmd) days.set(ymd, (days.get(ymd) ?? 0) + rows);
    SOON_HOURS.forEach((hours, index) => {
      if (startMs >= nowMs && startMs <= nowMs + hours * 3_600_000) soon[index] += rows;
    });
  }
  return {
    days: [...days.keys()].sort().map((value) => ({ value, label: dayLabel(value, todayYmd), count: live ? null : days.get(value)! })),
    soon: SOON_HOURS.map((hours, index) => ({ hours, label: soonLabel(hours), count: live ? null : soon[index] })),
  };
}

export function listedDays(days: readonly DayChoice[], time: GameTime, todayYmd = localTodayYmd()): readonly DayChoice[] {
  const listed = new Map(days.map((day) => [day.value, day]));
  for (const ymd of chosenGameDays(time, todayYmd)) {
    if (!listed.has(ymd)) listed.set(ymd, { value: ymd, label: dayLabel(ymd, todayYmd), count: 0 });
  }
  return [...listed.keys()].sort().map((ymd) => listed.get(ymd)!);
}

export const ANY_TIME_ID = "any";
export const LIVE_TIME_ID = "live";

export function soonId(hours: number): string {
  return `soon:${hours}`;
}

export function pickGameTime(time: GameTime, id: string, todayYmd = localTodayYmd()): GameTime {
  if (id === ANY_TIME_ID) return ANY_GAME_TIME;
  if (id === LIVE_TIME_ID) return LIVE_GAME_TIME;
  if (id.startsWith("soon:")) return { days: [], within: coerceStartsWithin(id.slice(5)), live: false };
  const days = new Set(chosenGameDays(time, todayYmd));
  if (days.has(id)) days.delete(id);
  else days.add(id);
  return { days: encodeGameDays(days, todayYmd), within: 0, live: false };
}
